import styles from './ProcessStepper.module.css';

// 반영 과정을 단계별로 보여주는 진행 스텝퍼 모달.
// 각 단계: pending(대기) → active(진행 중) → done(완료), 또는 failed(실패).
// 실패하면 그 단계를 빨강으로 표시하고 모달을 유지한다(자동으로 닫지 않음) —
// 사용자가 에러를 명확히 보고 직접 닫게 한다.

export type ProcessStatus = 'pending' | 'active' | 'done' | 'failed';

export interface ProcessStepItem {
  key: string;
  label: string;
  desc?: string;
  status: ProcessStatus;
}

interface ProcessStepperProps {
  // null이면 닫힘. 배열이면 모달 표시.
  steps: ProcessStepItem[] | null;
  title: string;
  // 진행이 멈춘 뒤(완료 또는 실패) 닫기 버튼으로 호출.
  onClose: () => void;
  // 서버에서 오래 도는 단계(예: Dagster run)는 진행 중에도 창을 닫을 수 있게 한다 — 실행은 계속된다.
  closableWhileActive?: boolean;
}

const CheckMark = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const CrossMark = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export default function ProcessStepper({ steps, title, onClose, closableWhileActive = false }: ProcessStepperProps) {
  if (!steps) return null;
  const hasActive = steps.some((s) => s.status === 'active');
  const hasFailed = steps.some((s) => s.status === 'failed');
  // 진행 중인 단계가 없으면(완료 또는 실패로 멈춤) 닫기 버튼 노출.
  const canClose = !hasActive || closableWhileActive;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.panel}>
        <div className={styles.title}>{title}</div>
        <ol className={styles.steps}>
          {steps.map((s, i) => (
            <li key={s.key} className={`${styles.step} ${styles[s.status]}`}>
              <span className={styles.rail} aria-hidden>
                <span className={styles.marker}>
                  {s.status === 'done' ? (
                    <CheckMark />
                  ) : s.status === 'failed' ? (
                    <CrossMark />
                  ) : s.status === 'active' ? (
                    <span className={styles.spinner} />
                  ) : (
                    <span className={styles.dot} />
                  )}
                </span>
                {i < steps.length - 1 && <span className={styles.line} />}
              </span>
              <span className={styles.body}>
                <span className={styles.label}>{s.label}</span>
                {s.desc && <span className={styles.desc}>{s.desc}</span>}
              </span>
            </li>
          ))}
        </ol>
        {canClose && (
          <button
            type="button"
            className={`${styles.closeBtn} ${hasFailed ? styles.closeBtnFailed : ''}`}
            onClick={onClose}
          >
            {hasActive ? '닫기 (백그라운드에서 계속)' : hasFailed ? '닫기' : '확인'}
          </button>
        )}
      </div>
    </div>
  );
}
